export const MAX_TOTAL = 4;

export function totalQuantity(quantities) {
  return Object.values(quantities).reduce((sum, q) => sum + q, 0);
}

export function canIncrease(quantities) {
  return totalQuantity(quantities) < MAX_TOTAL;
}

export function increase(quantities, productId) {
  if (!canIncrease(quantities)) return quantities;
  return { ...quantities, [productId]: (quantities[productId] || 0) + 1 };
}

export function decrease(quantities, productId, minQuantity) {
  const min = minQuantity || 0;
  const current = quantities[productId] || 0;
  if (current <= min) return quantities;
  return { ...quantities, [productId]: current - 1 };
}

export function promotionState(total) {
  if (total <= 1) return 1;
  if (total === 2) return 2;
  if (total === 3) return 3;
  return 4;
}

export function savingsCents(quantities, priceByProductId) {
  const total = totalQuantity(quantities);
  if (total < 2) return 0;
  let subtotal = 0;
  for (const [id, qty] of Object.entries(quantities)) {
    subtotal += (priceByProductId[id] || 0) * qty;
  }
  return Math.round(subtotal * 0.5);
}

export function halfPriceCents(priceCents) {
  return Math.round(priceCents * 0.5);
}

export function originalTotalCents(quantities, priceByProductId) {
  let total = 0;
  for (const [id, qty] of Object.entries(quantities)) {
    total += (priceByProductId[id] || 0) * qty;
  }
  return total;
}

export function discountedTotalCents(quantities, priceByProductId) {
  return originalTotalCents(quantities, priceByProductId) - savingsCents(quantities, priceByProductId);
}

// At total quantity 2 there is exactly one unit beyond the mandatory base
// unit — that's the "free" pair the promotion communicates. Either the base
// product itself was doubled, or exactly one other product was picked once.
export function freeModelId(quantities, currentProductId) {
  if ((quantities[currentProductId] || 0) >= 2) return currentProductId;
  for (const [id, qty] of Object.entries(quantities)) {
    if (id !== currentProductId && qty > 0) return id;
  }
  return null;
}

export function buildCartItems(quantities, variantByProductId) {
  const items = [];
  for (const [productId, qty] of Object.entries(quantities)) {
    const variantId = variantByProductId[productId];
    if (qty > 0 && variantId) {
      items.push({ id: variantId, quantity: qty });
    }
  }
  return items;
}

export function formatMoney(cents, currencyCode, locale) {
  try {
    return new Intl.NumberFormat(locale || 'en', {
      style: 'currency',
      currency: currencyCode,
    }).format(cents / 100);
  } catch {
    return (cents / 100).toFixed(2);
  }
}

const BaseElement = typeof HTMLElement !== 'undefined' ? HTMLElement : class {};

class WarblueBundleSelector extends BaseElement {
  connectedCallback() {
    this.currency = this.dataset.currency || 'EUR';
    this.locale = document.documentElement.lang || 'en';

    this.cards = Array.from(this.querySelectorAll('[data-wbb-card]'));
    this.priceByProductId = {};
    this.variantByProductId = {};
    this.quantities = {};
    this.currentProductId = null;

    this.cards.forEach((card) => {
      const id = card.dataset.productId;
      this.priceByProductId[id] = Number(card.dataset.price);
      this.variantByProductId[id] = Number(card.dataset.variantId);
      const isCurrent = card.dataset.isCurrent === 'true';
      const initialQty = Number(card.dataset.initialQty) || 0;
      // On the product's own PDP, it always starts at (at least) 1. On a
      // bundle page, none of the 4 slots is "current", so every card starts
      // at whatever quantity that real bundle composition gives it (0 if not
      // included) instead of always 0.
      this.quantities[id] = isCurrent ? Math.max(1, initialQty) : initialQty;
      if (isCurrent) this.currentProductId = id;

      card.addEventListener('click', (event) => {
        if (event.target.closest('[data-wbb-decrease], [data-wbb-increase]')) return;
        if (card.dataset.available === 'false') return;
        const qty = this.quantities[id] || 0;

        if (id === this.currentProductId) {
          // base product: tap toggles between its floor (1) and a 2nd unit of
          // itself — a quick way to pick "the same model twice" as the free
          // pair. Quantities raised above 2 via +/- also collapse back to 1.
          if (qty <= 1 && canIncrease(this.quantities)) {
            this.setQuantities(increase(this.quantities, id));
          } else if (qty > 1) {
            this.setQuantities({ ...this.quantities, [id]: 1 });
          }
          return;
        }

        if (qty === 0) {
          this.setQuantities(increase(this.quantities, id));
        } else {
          // already-selected, non-base tile: tapping the body fully deselects it
          this.setQuantities({ ...this.quantities, [id]: 0 });
        }
      });

      const decreaseBtn = card.querySelector('[data-wbb-decrease]');
      const increaseBtn = card.querySelector('[data-wbb-increase]');
      const minQty = isCurrent ? 1 : 0;

      decreaseBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        this.setQuantities(decrease(this.quantities, id, minQty));
      });
      increaseBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        this.setQuantities(increase(this.quantities, id));
      });
    });

    // Verified working pattern from another live Shopify store's own bundle
    // selector (Eternas Beauty): intercept the CLICK on the submit button
    // itself, in the capture phase, with stopImmediatePropagation. This runs
    // — and can cancel — before the click's default action even triggers the
    // 'submit' event, so the theme's own submit handler (assets/theme.js,
    // attached directly on the form) never fires at all for this click.
    //
    // Crucially, the target form is resolved FRESH from the clicked button
    // every time, never cached — this page's <product-rerender> can replace
    // the buy-buttons form after initial load, and an earlier version of
    // this fix compared against a form reference captured once in
    // connectedCallback, which could go stale and silently fail to match,
    // letting the native single-item submit through with no error.
    this.submitting = false;
    this.onDocumentClick = (event) => {
      const submitButton = event.target.closest('button[type="submit"]');
      if (!submitButton) return;
      if (!this.isConnected) return;
      const form = submitButton.closest('form');
      if (!form || !form.action || !form.action.includes('/cart/add')) return;
      if (totalQuantity(this.quantities) <= 1) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.submitBundle(form);
    };
    document.addEventListener('click', this.onDocumentClick, true);

    this.render();
  }

  disconnectedCallback() {
    if (this.onDocumentClick) {
      document.removeEventListener('click', this.onDocumentClick, true);
    }
  }

  setQuantities(next) {
    this.quantities = next;
    this.render();
  }

  render() {
    this.cards.forEach((card) => {
      const id = card.dataset.productId;
      const qty = this.quantities[id] || 0;
      const qtyEl = card.querySelector('[data-wbb-qty]');
      const qtyValueEl = card.querySelector('[data-wbb-qty-value]');
      const decreaseBtn = card.querySelector('[data-wbb-decrease]');
      const increaseBtn = card.querySelector('[data-wbb-increase]');
      const minQty = id === this.currentProductId ? 1 : 0;

      card.classList.toggle('is-selected', qty > 0);
      qtyEl.classList.toggle('is-active', qty > 0);
      qtyValueEl.textContent = String(qty);
      decreaseBtn.disabled = qty <= minQty;
      increaseBtn.disabled = !canIncrease(this.quantities);
    });

    const total = totalQuantity(this.quantities);
    const state = promotionState(total);

    this.querySelectorAll('[data-wbb-state]').forEach((el) => {
      el.hidden = Number(el.dataset.wbbState) !== state;
    });

    if (state === 2) {
      const freeId = freeModelId(this.quantities, this.currentProductId);
      this.querySelectorAll('[data-wbb-free-model]').forEach((el) => {
        el.hidden = el.dataset.wbbFreeModel !== String(freeId);
      });
    }

    if (state >= 2) {
      const savings = savingsCents(this.quantities, this.priceByProductId);
      this.querySelectorAll('[data-wbb-savings]').forEach((el) => {
        el.textContent = formatMoney(savings, this.currency, this.locale);
      });

      const discounted = discountedTotalCents(this.quantities, this.priceByProductId);
      const original = originalTotalCents(this.quantities, this.priceByProductId);
      this.querySelectorAll('[data-wbb-discounted-total]').forEach((el) => {
        el.textContent = formatMoney(discounted, this.currency, this.locale);
      });
      this.querySelectorAll('[data-wbb-original-total]').forEach((el) => {
        el.textContent = formatMoney(original, this.currency, this.locale);
      });
    }

    // On a bundle page nothing is "current" (currentProductId is null), so
    // fall back to any configured product's price — all 4 models share the
    // same price, so this is exactly as correct as using "the current one".
    // Guards against showing a broken "$NaN" when there's genuinely no price
    // to reference yet, instead of silently rendering an error to customers.
    const referencePrice = this.currentProductId != null
      ? this.priceByProductId[this.currentProductId]
      : Object.values(this.priceByProductId)[0];

    if (state === 1) {
      this.querySelectorAll('[data-wbb-current-price]').forEach((el) => {
        el.textContent = Number.isFinite(referencePrice) ? formatMoney(referencePrice, this.currency, this.locale) : '';
      });
    }

    if (state === 2 || state === 3) {
      const half = halfPriceCents(referencePrice);
      this.querySelectorAll('[data-wbb-half-price]').forEach((el) => {
        el.textContent = Number.isFinite(half) ? formatMoney(half, this.currency, this.locale) : '';
      });
    }

  }

  // Faithful reimplementation of the theme's own ProductForm._onSubmit
  // (assets/theme.js), swapping its single id/quantity FormData body for a
  // JSON { items: [...] } body so the whole bundle is added in one request.
  // Dispatches the exact same variant:add/cart:change/cart:error events so
  // the cart drawer, cart count, and cart sections update identically.
  async submitBundle(form) {
    if (this.submitting) return;
    this.submitting = true;

    const submitButtons = Array.from(form.elements).filter((el) => el.type === 'submit');
    submitButtons.forEach((btn) => btn.setAttribute('aria-busy', 'true'));

    // Other theme components (e.g. the cart drawer) listen for this event and
    // PUSH their own section id into this same array before the request is
    // built — mirroring that mutation is required, or their section HTML
    // never gets requested and they crash trying to update with nothing.
    const sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle },
    }));

    const items = buildCartItems(this.quantities, this.variantByProductId);
    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sectionsToBundle.join(',') }),
      });

      submitButtons.forEach((btn) => btn.removeAttribute('aria-busy'));
      this.submitting = false;

      const responseJson = await response.json();

      if (response.ok) {
        if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = `${root}cart`;
          return;
        }
        const cartContent = await (await fetch(`${root}cart.js`)).json();
        cartContent.sections = responseJson.sections;
        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: Object.prototype.hasOwnProperty.call(responseJson, 'items') ? responseJson.items : [responseJson],
            cart: cartContent,
          },
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent },
        }));
      } else {
        form.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: responseJson.description },
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (err) {
      submitButtons.forEach((btn) => btn.removeAttribute('aria-busy'));
      this.submitting = false;
    }
  }
}

if (typeof customElements !== 'undefined') {
  customElements.define('warblue-bundle-selector', WarblueBundleSelector);
}
